<template>
  <div class="activities">
    <h2>Ostatnia aktywność</h2>
    <div class="activity">
      <div>
        <div class="activity__content">
          <div class="activity__label">
            <h3>Jadłospisy</h3>
          </div>
          <div v-if="!getDashboardLastActivity('diets')?.length" class="activity__empty">
            <Space align-y="center" :space="8" :padding-x="5" :padding-y="12">
              <p>Dodaj swój pierwszy jadłospis</p>
              <StyledButton variant="light" size="big" @click="openAddDietModal">
                Utwórz jadłospis
              </StyledButton>
            </Space>
          </div>
          <div v-if="getDashboardLastActivity('diets')?.length" class="activity__list">
            <router-link
              v-for="(item, index) in getDashboardLastActivity('diets')"
              :key="index"
              class="activity__list__item"
              :to="{
                name: 'DietDetailWeek',
                params: { dietType: item.type, dietId: item.id }
              }"
            >
              <Space direction="columns" align-y="center" :space="8">
                <BaseIcon size="20" name="assignment" type="secondary" />
                {{ item.name }}
              </Space>
            </router-link>
          </div>
        </div>
      </div>
      <div>
        <div class="activity__content">
          <div class="activity__label">
            <h3>Pacjenci</h3>
          </div>
          <div v-if="!getDashboardLastActivity('patients')?.length" class="activity__empty">
            <Space align-y="center" :space="8" :padding-x="5" :padding-y="12">
              <p>Dodaj swojego pierwszego pacjenta</p>
              <StyledButton variant="light" size="big" @click="openAddPatientModal">
                Dodaj pacjenta
              </StyledButton>
            </Space>
          </div>
          <div v-if="getDashboardLastActivity('patients')?.length" class="activity__list">
            <a
              v-for="(item, index) in getDashboardLastActivity('patients')"
              :key="index"
              class="activity__list__item"
              :href="item.url"
            >
              <Space direction="columns" align-y="center" :space="8">
                <BaseIcon size="20" name="people_alt" type="secondary" />
                {{ item.patient }}
              </Space>
            </a>
          </div>
        </div>
      </div>
    </div>
    <DashboardAddDietModal v-if="isOpenAddDietModal" @close="closeAddDietModal()" />
    <DashboardAddPatientModal v-if="isOpenAddPatientModal" @close="closeAddPatientModal()" />
  </div>
</template>

<script lang="ts" setup>
  import { G } from '@mobily/ts-belt'
  import { computed, ref } from 'vue'

  import BaseIcon from '@/components/BaseIcon.vue'
  import { Space } from '@/components/Space'
  import { StyledButton } from '@/components/StyledButton'
  import DashboardAddDietModal from '@/pages/dashboard/components/DashboardAddDietModal.vue'
  import DashboardAddPatientModal from '@/pages/dashboard/components/DashboardAddPatientModal.vue'
  import { useDashboardStore } from '@/store/dashboardStore'

  const dashboardState = useDashboardStore()
  const isOpenAddDietModal = ref(false)
  const isOpenAddPatientModal = ref(false)
  const getDashboardLastActivity = computed(() => dashboardState.getDashboardLastActivity)

  const closeAddDietModal = () => {
    isOpenAddDietModal.value = false
  }
  const openAddDietModal = () => {
    if (G.isNotNullable(dataLayer) && G.isArray(dataLayer)) {
      dataLayer.push({
        button_location: 'dashboard_button',
        event: 'create_diet_button_clicked',
      })
    }
    isOpenAddDietModal.value = true
  }
  const closeAddPatientModal = () => {
    isOpenAddPatientModal.value = false
  }
  const openAddPatientModal = () => {
    isOpenAddPatientModal.value = true
  }
</script>

<style scoped>
  h2 {
    margin: 0 0 9px;
    font-family: Montserrat-Medium;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px;
    text-align: left;
    letter-spacing: -0.017em;
  }

  .activities {
    padding-top: 28px;
  }

  .activity {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .activity > div {
    width: calc(50% - 14px);
  }

  .activity__content {
    padding: 24px;
    background: #f7f7f7;
    border-radius: 8px;
  }

  .activity__content .activity__label {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .activity__content .activity__label h3 {
    margin: 0;
    font-family: Montserrat-Medium;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    text-align: left;
    letter-spacing: -0.006em;
  }

  .activity__content .activity__list {
    display: flex;
    flex-direction: column;
    margin-top: 24px;
  }

  .activity__content .activity__list__item {
    position: relative;
    display: block;
    align-items: center;
    padding: 13px 16px;
    margin-top: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    color: var(--color-primary-text);
    text-align: left;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    background: #fff;
    border-radius: 8px;
  }

  .activity__content .activity__list__item:hover {
    background: var(--color-main-20);
  }

  .activity__content .activity__list > .activity__list__item:first-child {
    margin-top: 0;
  }

  .activity__content .activity__list__item :deep(.material-icon__image) {
    width: 20px;
  }

  .activity__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 142px;
    margin-top: 22px;
    background: #fff;
    border: 1px dashed #ccc;
    border-radius: 8px;
  }

  .activity__empty p {
    margin: 0;
    font-family: Montserrat-Medium;
    font-size: 14px;
    font-style: normal;
    line-height: 20px;
    color: #8d8d8d;
    text-align: center;
    letter-spacing: -0.006em;
  }

  .activity__empty .add-item {
    padding: 8px 16px;
    font-family: Montserrat-Medium;
    font-size: 14px;
    font-style: normal;
    line-height: 20px;
    color: var(--color-main-100);
    text-align: left;
    letter-spacing: -0.006em;
    text-decoration: none;
    cursor: pointer;
    background: var(--color-main-10);
    border-radius: 4px;
  }

  .activity__empty .add-item:hover {
    color: #008183;
  }

  .activity__empty > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .activities :deep(.content) > .content__row:first-child {
    margin-top: 16px;
  }

  .activities :deep(.content__row--double) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .activities :deep(.content__row--double) > div {
    position: relative;
    width: 248px;
  }

  .activities :deep(.content__row__select) {
    position: relative;
  }

  .activities :deep(.content__row__buttons) {
    display: flex;
    justify-content: right;
  }

  .activities :deep(.content__row) {
    position: relative;
    margin-top: 28px;
  }

  /* errors */

  .ppdo p {
    padding: 0 10px;
    margin-bottom: 10px;
  }

  @media screen and (width <= 1400px) {
    .ppdo p {
      margin-bottom: 5px;
      font-size: 12px;
      line-height: 15px;
    }

    .ppdo a {
      font-size: 12px;
    }
  }
</style>
